import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import { CloudUpload, CheckCircle, ErrorOutline, Pending, ContentCopy, Add, FileDownload } from '@mui/icons-material';

import { ImportContactsData } from '../data/useImportContacts';
import { ContactTags } from './ContactTags';
import { useCopyText } from '../../primitives';
import { SmallAddButton } from './Primitives';
import { FormControlsForFields } from '../../schemed';
import { Schema } from '../../../hooks/useSchema';

export interface ImportContactsDialogProps {
  data: ImportContactsData;
  schema: Schema;
  onComplete?: () => void;
}

export const ImportContactsDialog = ({ data, onComplete, schema }: ImportContactsDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const copyText = useCopyText();

  // Local state for revealing the additional parameter fields
  const [showConsentPd, setShowConsentPd] = useState(false);
  const [showConsentEmails, setShowConsentEmails] = useState(false);
  const [showCommunicationLanguage, setShowCommunicationLanguage] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  const {
    importState,
    isOpen,
    isLoading,
    rows,
    headers,
    rowStates,
    mappableFields,
    mapping,
    params,
    cancel,
    goBack,
    uploadFile,
    updateMapping,
    updateParams,
    doImport,
    downloadErrors,
  } = data;

  // Calculate progress stats
  const totalRows = rows.length;
  const processedRows = Object.values(rowStates).filter((s) => s.status !== 'pending').length;
  const successRows = Object.values(rowStates).filter((s) => s.status === 'success').length;
  const errorRows = Object.values(rowStates).filter((s) => s.status === 'error').length;

  // Dynamic Title
  let title = 'Импорт контактов';
  if (importState === 'map_fields') title = `Настройка полей импорта (${totalRows} строк)`;
  if (importState === 'importing') title = `Импорт... (${processedRows} / ${totalRows})`;
  if (importState === 'done') title = 'Импорт завершен';

  // Prevent closing accidentally while actively importing
  const handleClose = (e: React.SyntheticEvent, reason: string) => {
    if (importState === 'importing') return;
    
    // Reset local UI state on close
    setShowConsentPd(false);
    setShowConsentEmails(false);
    setShowCommunicationLanguage(false);
    setShowCategories(false);
    cancel();
  };

  // --- File Upload Handlers ---
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      uploadFile(file);
    }
  };

  const handleCopyErrors = () => {
    const errorLog = rows
      .map((row, index) => ({ row, state: rowStates[index] }))
      .filter(({ state }) => state?.status === 'error')
      .map(({ row, state }) => `Ошибка: ${state.errorMsg}\nДанные: ${JSON.stringify(row)}`)
      .join('\n\n');

    copyText(errorLog);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        {/* ================= STAGE 1: UPLOAD ================= */}
        {importState === 'upload' && (
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={6}
            gap={2}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : 'divider',
              borderRadius: 2,
              bgcolor: isDragging ? 'primary.50' : 'background.paper',
              transition: 'all 0.2s ease',
            }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 60, color: isDragging ? 'primary.main' : 'text.secondary' }} />
                <Typography variant="body1" textAlign="center" color="textSecondary">
                  Перетащите файл Excel (.xlsx) сюда или выберите на компьютере
                </Typography>
                <Button variant="contained" component="label" sx={{ mt: 1 }}>
                  Выбрать файл
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    hidden
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />
                </Button>
              </>
            )}
          </Box>
        )}

        {/* ================= STAGE 2: MAP FIELDS ================= */}
        {importState === 'map_fields' && (
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
            {/* --- LEFT COLUMN: Global parameters overrides --- */}
            <Box flex={1} sx={{ minWidth: 250 }}>
              <Typography variant="subtitle2" gutterBottom>
                Присвоить всем импортируемым контактам (перезапишет данные файла):
              </Typography>
              
              <Stack spacing={1.5} mt={1}>
                {/* 1. Tags */}
                <Box>
                  <ContactTags
                    tagIds={params.tags}
                    onUpdateTags={(newTagIds) => updateParams({ tags: newTagIds.map(Number) })}
                  />
                </Box>

                {/* 2. Categories */}
                <Box>
                  {!showCategories ? (
                    <SmallAddButton icon={<Add />} onClick={() => setShowCategories(true)}>
                      категории
                    </SmallAddButton>
                  ) : (
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <FormControlsForFields
                        data={params}
                        onChange={(o,c) => updateParams(c)}
                        schema={schema}
                        fields={[
                          ["categories", { autoComplete: true }]
                        ]}
                        />
                    </Box>
                  )}
                </Box>

                {/* 3. Consent PD */}
                <Box>
                  {!showConsentPd ? (
                    <SmallAddButton icon={<Add />} onClick={() => setShowConsentPd(true)}>
                      согласие ПД
                    </SmallAddButton>
                  ) : (
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <FormControlsForFields
                        fields={[
                          ["consent_pd", { controlProps: { autoFocus: true }}],
                        ]}
                        schema={schema}
                        data={params}
                        onChange={(o, c) => updateParams(c)}
                      />
                    </Box>
                  )}
                </Box>

                {/* 4. Consent Emails */}
                <Box>
                  {!showConsentEmails ? (
                    <SmallAddButton iconOnly={false} icon={<Add />} onClick={() => setShowConsentEmails(true)}>
                      согласие на рассылки
                    </SmallAddButton>
                  ) : (
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <FormControlsForFields
                        fields={[
                          ["consent_emails", { controlProps: { autoFocus: true }}],
                        ]}
                        schema={schema}
                        data={params}
                        onChange={(o, c) => updateParams(c)}
                      />
                    </Box>
                  )}
                </Box>

                {/* 5. Communication Language */}
                <Box>
                  {!showCommunicationLanguage ? (
                    <SmallAddButton iconOnly={false} icon={<Add />} onClick={() => setShowCommunicationLanguage(true)}>
                      язык коммуникации
                    </SmallAddButton>
                  ) : (
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <FormControlsForFields
                        fields={[
                          ["communication_language", { controlProps: { autoFocus: true }}],
                        ]}
                        schema={schema}
                        data={params}
                        onChange={(o, c) => updateParams(c)}
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
            <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

            {/* --- RIGHT COLUMN: Field Mapping --- */}
            <Box flex={2} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" gutterBottom>
                Сопоставление колонок из файла:
              </Typography>
              
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                <Table size="small" stickyHeader sx={{ '& td, & th': { borderBottom: 'none' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 0, bgcolor: 'background.paper' }}>Колонка в файле</TableCell>
                      <TableCell sx={{ pr: 0, bgcolor: 'background.paper' }}>Поле в CRM</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {headers.map((header) => (
                      <TableRow key={header}>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', pl: 0 }}>
                          {header}
                        </TableCell>
                        <TableCell sx={{ pr: 0 }}>
                          <Autocomplete
                            size="small"
                            fullWidth
                            options={mappableFields}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            value={mappableFields.find(f => f.value === mapping[header]) || null}
                            onChange={(e, newValue) => updateMapping(header, newValue ? newValue.value : null)}
                            renderInput={(params) => (
                              <TextField 
                                {...params} 
                                placeholder="-- Пропустить --" 
                              />
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Box>
        )}

        {/* ================= STAGE 3 & 4: IMPORTING & DONE ================= */}
        {(importState === 'importing' || importState === 'done') && (
          <Stack spacing={2}>
            {/* Progress indicators */}
            {importState === 'importing' && (
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Обработка записей...
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {Math.round((processedRows / totalRows) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={(processedRows / totalRows) * 100} />
              </Box>
            )}

            {/* Summary for Done state */}
            {importState === 'done' && (
              <Alert severity={errorRows === 0 ? 'success' : 'warning'}>
                Успешно: {successRows} | Ошибок: {errorRows}
              </Alert>
            )}

            {/* Live Rows Feed */}
            <Box
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                bgcolor: 'background.default',
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack spacing={1}>
                {rows.map((row, index) => {
                  const state = rowStates[index];
                  // First column value as an identifier, fallback to row index
                  const rowLabel = row[headers[0]] || `Строка ${index + 2}`; 

                  return (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1 }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
                        {rowLabel}
                      </Typography>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        {state?.status === 'pending' && <Pending color="disabled" fontSize="small" />}
                        {state?.status === 'success' && <CheckCircle color="success" fontSize="small" />}
                        {state?.status === 'error' && (
                          <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 150 }}>
                            {state.errorMsg}
                          </Typography>
                        )}
                        {state?.status === 'error' && <ErrorOutline color="error" fontSize="small" />}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {/* Download & Copy Errors Buttons (only on DONE and if there are errors) */}
        {importState === 'done' && errorRows > 0 && (
          <Box display="flex" gap={1} mr="auto">
            <Button 
              onClick={downloadErrors} 
              color="error" 
              startIcon={<FileDownload />} 
            >
              Скачать ошибки
            </Button>
            <Button 
              onClick={handleCopyErrors} 
              color="error" 
              startIcon={<ContentCopy />} 
            >
              Копировать
            </Button>
          </Box>
        )}

        {importState === 'upload' && (
          <Button onClick={e => handleClose(e, "cancel")} color="inherit">
            Отмена
          </Button>
        )}

        {importState === 'map_fields' && (
          <>
            <Button onClick={goBack} color="inherit">
              Назад
            </Button>
            <Button onClick={doImport} variant="contained" color="primary">
              Начать импорт
            </Button>
          </>
        )}

        {importState === 'done' && (
          <Button
            onClick={() => {
              if(onComplete) {
                onComplete();
              }
              handleClose({} as any, 'done');
            }}
            variant="contained"
            color="primary">
            Закрыть
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};